import { getHelper } from '@/devtools/utils';
import { VersicherungsnehmerPropertyContractSchema } from '@/features/Clients/Schemas/Versicherungsnehmer';
import type { ComparisonDetail } from '@/features/Comparison/Components/types';
import { SEARCH_DATA_CONTEXT } from '@/features/Global/types';
import type { SpartenConfig } from '@/features/Sparten/types';
import { CalculationRelevantEntityFields } from '@/features/Sparten/types';
import { formatCurrency } from '@/utils/formatters/euro';
import type { OPTIONAL_ROLE, ROLE, Role } from '@/features/Clients';
import { useStorableState } from '@/utils/useStorableState/useStorableState';
import { dehydrateRequestData } from '@/utils/dehydrateRequestData/dehydrateRequestData';
import {
  type InputStore,
  useUnfInputStore,
} from '@/features/Sparten/unf/useInputStore';
import { getUNFInputDehydrationDefinitions } from '@/features/Sparten/unf/inputDehydration';
import { formatYears } from '@/utils/formatters/years';
import { formatChoice } from '@/utils/formatters/simpleChoice';
import { SIMPLE_CHOICE } from '@/features/ChoicesLists/Simple/types';
import {
  generateUnfFieldPathCallback,
  usedRoles,
} from '@/features/Sparten/unf/utils';
import { formatPercent } from '@/utils/formatters/number';
import string from '@/utils/formatters/string';
import { FIELD_PATH_CONTEXT } from '@/features/Comparison/utils';
import {
  KEEP_DATA_FROM_QUOTE_MAPPING,
  keepDataFromQuoteMappings,
} from '@/features/Sparten/shared/keepDataFromQuoteMappings';
import { getUNFContractDehydrationDefinitions } from '@/features/Sparten/unf/contractDehydration';
import boolean from '@/utils/formatters/boolean';
import { ENTITY_TYPE } from '@/features/EntityManager/types';
import { UNFVPSchema } from '@/features/Sparten/unf/schema/vpSchema/vpSchema';
import image from './assets/bg.jpg';
import { unfVersicherungsangabenConfig } from '@/features/Sparten/unf/versicherungsangabenConfig';

export const config: SpartenConfig = {
  component: () => import('@/views/Base/Layout.vue'),
  path: '/unf',

  props: {
    name: 'Unfallversicherung',
    sparte: 'unf',
    image,
  },

  general: {
    distribution: {
      channel: true,
      pooling: true,
    },

    maxSimultaneousCount: 3,

    hasRisikoDEMVerCertificate: true,

    versicherungsangabenConfig: unfVersicherungsangabenConfig,

    clientFields: {
      versicherungsnehmer: {
        geburtsdatum: 'geburtsdatum',
        plz: 'adresse.plz',
      },
      versichertePerson: {
        geburtsdatum: 'geburtsdatum',
        plz: 'adresse.plz',
      },
      versichertePerson2: {
        geburtsdatum: 'geburtsdatum',
        plz: 'adresse.plz',
      },
      versichertePerson3: {
        geburtsdatum: 'geburtsdatum',
        plz: 'adresse.plz',
      },
      versichertePerson4: {
        geburtsdatum: 'geburtsdatum',
        plz: 'adresse.plz',
      },
      versichertePerson5: {
        geburtsdatum: 'geburtsdatum',
        plz: 'adresse.plz',
      },
      versichertePerson6: {
        geburtsdatum: 'geburtsdatum',
        plz: 'adresse.plz',
      },
    },

    calculationRelevantEntityFields: (): CalculationRelevantEntityFields => {
      const fields: CalculationRelevantEntityFields = {
        [ENTITY_TYPE.CLIENT]: {},
      };

      usedRoles.value.forEach((role) => {
        fields[ENTITY_TYPE.CLIENT]![role] = ['geburtsdatum', 'adresse.plz'];
      });

      return fields;
    },

    profileFields: [
      'versichertePerson.leistungsumfang.invaliditaetsSumme',
      'versichertePerson.leistungsumfang.progression',
      'versichertePerson.leistungsumfang.todesfallSumme',
      'versichertePerson.leistungsumfang.monatlicheUnfallRente',
      'versichertePerson.leistungsumfang.bergungskosten',
      'versichertePerson.leistungsumfang.operationen',
      'versichertePerson.leistungsumfang.uebergangsleistung',
      'versichertePerson.leistungsumfang.krankenhaustagegeld.enabled',
      'versichertePerson.leistungsumfang.krankenhaustagegeld.hoehe',
      'versichertePerson.leistungsumfang.krankenhaustagegeld.genesungsgeldInGleicherHoehe',
      'versichertePerson.leistungsumfang.krankentagegeld.enabled',
      'versichertePerson.leistungsumfang.krankentagegeld.hoehe',
      'versichertePerson.leistungsumfang.krankentagegeld.tag',
    ],

    relevantVPRoles: [
      'versichertePerson' as ROLE,
      'versichertePerson2' as OPTIONAL_ROLE,
      'versichertePerson3' as OPTIONAL_ROLE,
      'versichertePerson4' as OPTIONAL_ROLE,
      'versichertePerson5' as OPTIONAL_ROLE,
      'versichertePerson6' as OPTIONAL_ROLE,
    ],

    postLoadingSessionState: (data) => {
      const store = useUnfInputStore();
      setTimeout(() => {
        usedRoles.value.forEach((role) => {
          if (
            store[role] !== undefined &&
            data.state.input[role] !== undefined
          ) {
            store[role].personenDaten.beruf = (
              data.state.input[role] as UNFVPSchema
            ).personenDaten.beruf;
          }
        });
      }, 300);
    },

    buildInputDataObject: (context: SEARCH_DATA_CONTEXT, dehydrate = false) => {
      const state = useUnfInputStore().$state as InputStore;

      const preparedData = useStorableState().create(state);

      if (context === SEARCH_DATA_CONTEXT.TARIFF && dehydrate) {
        return dehydrateRequestData(
          preparedData,
          getUNFInputDehydrationDefinitions,
        );
      }

      return preparedData;
    },

    buildContractDataObject: (state) =>
      dehydrateRequestData(state, getUNFContractDehydrationDefinitions()),

    companyQuestionComponent: () =>
      import(
        '@/features/Sparten/unf/sections/contract/ContractCompanyQuestions.vue'
      ),

    comparisonComponents: {
      TariffServiceComponent: () =>
        import(
          '@/features/Sparten/unf/components/ComparisonTariffService/ComparisonTariffService.vue'
        ),
    },

    comparison: {
      getDetails: () => {
        const map = new Map<string, ComparisonDetail>();

        map.set('betrag', {
          label: 'zu zahlender Beitrag',
          format: formatCurrency,
        });

        return map;
      },
      serviceFields: [
        {
          label: 'Laufzeit',
          fieldPath: 'laufzeit',
          formatter: formatYears,
        },
        {
          label: 'Zahlweise',
          fieldPath: 'zahlweise',
          formatter: formatChoice(SIMPLE_CHOICE.PAYMENT_PERIOD),
        },
        {
          label: 'Verzicht auf Gesundheitsfragen',
          inputFieldPath: 'gesundheitsfragenVerzicht',
          fieldPath: 'gesundheitsfragen.merkmalTariffText',
          inputFormatter: boolean,
          formatter: string,
        },
        {
          label: 'Risikogruppe',
          fieldPath: generateUnfFieldPathCallback(
            'personenDaten.risikogruppe',
            'risikogruppe',
          ),
          formatter: string,
          hasClientPrefix: true,
        },
        {
          label: 'Beruf',
          fieldPath: generateUnfFieldPathCallback(
            'personenDaten.beruf',
            'beruf',
          ),
          formatter: string,
          hasClientPrefix: true,
          enforceContext: FIELD_PATH_CONTEXT.INPUT,
        },
        {
          label: 'Grundinvalidität',
          fieldPath: generateUnfFieldPathCallback(
            'leistungsumfang.invaliditaetsSumme',
            'invaliditaetsHoehe',
          ),
          formatter: formatCurrency,
          hasClientPrefix: true,
        },
        {
          label: 'Progression',
          fieldPath: generateUnfFieldPathCallback(
            'leistungsumfang.progression',
            'progression',
          ),
          inputFormatter: formatChoice(SIMPLE_CHOICE.PROGRESSION),
          formatter: formatPercent,
          hasClientPrefix: true,
        },
        {
          label: 'Todesfallsumme',
          fieldPath: generateUnfFieldPathCallback(
            'leistungsumfang.todesfallSumme',
            'todesfallHoehe',
          ),
          formatter: formatCurrency,
          hasClientPrefix: true,
        },
        {
          label: 'Monatliche Unfallrente',
          fieldPath: generateUnfFieldPathCallback(
            'leistungsumfang.monatlicheUnfallRente',
            'monatlicheUnfallRenteHoehe',
          ),
          formatter: formatCurrency,
          hasClientPrefix: true,
        },
        {
          label: 'Krankenhaustagegeld',
          fieldPath: generateUnfFieldPathCallback(
            'leistungsumfang.krankenhaustagegeld.hoehe',
            'krankenhaustagegeldHoehe',
          ),
          formatter: formatCurrency,
          hasClientPrefix: true,
        },
        {
          label: 'Genesungsgeld in gleicher Höhe wie Krankenhaustagegeld',
          fieldPath: generateUnfFieldPathCallback(
            'leistungsumfang.krankenhaustagegeld.genesungsgeldInGleicherHoehe',
            'genesungsgeldHoehe',
          ),
          inputFormatter: boolean,
          formatter: formatCurrency,
          customRequirement: (role: Role) => {
            return useUnfInputStore()[role].leistungsumfang.krankenhaustagegeld
              .enabled;
          },
          hasClientPrefix: true,
        },
        {
          label: 'Krankentagegeld',
          fieldPath: generateUnfFieldPathCallback(
            'leistungsumfang.krankentagegeld.hoehe',
            'krankentagegeldHoehe',
          ),
          formatter: formatCurrency,
          hasClientPrefix: true,
        },
        {
          label: 'Krankentagegeld ab Tag',
          fieldPath: generateUnfFieldPathCallback(
            'leistungsumfang.krankentagegeld.tag',
            'krankentagegeldTag',
          ),
          formatter: string,
          inputFormatter: formatChoice(SIMPLE_CHOICE.KRANKENTAGEGELD_AB_TAG),
          customRequirement: (role: Role) => {
            return useUnfInputStore()[role].leistungsumfang.krankentagegeld
              .enabled;
          },
          hasClientPrefix: true,
        },
        {
          label: 'Übergangsleistung',
          fieldPath: generateUnfFieldPathCallback(
            'leistungsumfang.uebergangsleistung',
            'uebergangsleistungHoehe',
          ),
          formatter: formatCurrency,
          hasClientPrefix: true,
        },
        {
          label: 'Kosmetische Operationen',
          fieldPath: generateUnfFieldPathCallback(
            'leistungsumfang.operationen',
            'operationenHoehe',
          ),
          formatter: formatCurrency,
          inputFormatter: formatChoice(SIMPLE_CHOICE.OPERATIONEN),
          hasClientPrefix: true,
        },
        {
          label: 'Bergungskosten',
          fieldPath: generateUnfFieldPathCallback(
            'leistungsumfang.bergungskosten',
            'bergungskostenHoehe',
          ),
          formatter: formatCurrency,
          inputFormatter: formatChoice(SIMPLE_CHOICE.BERGUNGSKOSTEN),
          hasClientPrefix: true,
        },
      ],
    },

    keepDataFromQuote: {
      clients: {
        versichertePerson: [
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.ZIP_CODE],
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.BIRTHDAY],
        ],
        versichertePerson2: [
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.ZIP_CODE],
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.BIRTHDAY],
        ],
        versichertePerson3: [
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.ZIP_CODE],
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.BIRTHDAY],
        ],
        versichertePerson4: [
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.ZIP_CODE],
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.BIRTHDAY],
        ],
        versichertePerson5: [
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.ZIP_CODE],
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.BIRTHDAY],
        ],
        versichertePerson6: [
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.ZIP_CODE],
          ...keepDataFromQuoteMappings[KEEP_DATA_FROM_QUOTE_MAPPING.BIRTHDAY],
        ],
      },
    },

    tariffComponent: () =>
      import(
        '@/features/Sparten/unf/components/TariffDetails/TariffDetails.vue'
      ),

    baseTariffComponent: () =>
      import('@/features/Sparten/unf/components/TariffDetails/BaseTariff.vue'),

    // @todo: Muss später auf UNF angepasst werden
    devtools: {
      directAccess: {
        input: {
          icon: 'edit',
          setup: async () => getHelper('phv').resetState(),
        },
        offers: {
          icon: 'list',
          setup: async () => getHelper('phv').selectAllFeatures(),
        },
        comparison: {
          icon: 'compare_arrows',
          setup: async () => getHelper('phv').loadTariffs(),
        },
        contract: {
          icon: 'description',
          setup: async () => getHelper('phv').showContract(),
        },
      },
    },

    contractVnSchema: () => VersicherungsnehmerPropertyContractSchema,
    blockCompanyClient: true,
  },

  // Wizard steps
  steps: {
    input: {
      component: () => import('@/features/Sparten/unf/views/InputView.vue'),
    },

    offers: {
      component: () => import('@/features/Sparten/unf/views/Tariffs.vue'),
    },

    comparison: {
      component: () => import('@/features/Sparten/unf/views/Comparison.vue'),
    },

    contract: {
      component: () => import('@/features/Sparten/unf/views/ContractView.vue'),
    },

    completion: {
      component: () => import('@/views/Layouts/Completion/Completion.vue'),
    },
  },
};

export default config;
